import React, { useState, useMemo } from 'react';
import { cryptoPairLabel } from '../composables/fetchCrypto';
import { toast } from 'react-hot-toast';
// ✅ 1. Import Context เข้ามา
import { usePortfolio } from '../context/PortfolioContext';
import { holdingQuoteSymbol } from '../utils/portfolioType';

interface SignalManagementPopupProps {
  isOpen: boolean;
  onClose: () => void;
  updates: any[];
  // ❌ ลบ portfolios: any[]; ออกจากตรงนี้
  onImportSelected: (tickers: string[], isBulk: boolean, shouldUpdateSync: boolean) => void;
  onRefresh: (isFull?: boolean) => void;
  isLoading?: boolean;
  allHistoryData?: any[]; 
}
type TabType = 'updates' | 'all_history' | 'my_portfolio' | 'hidden';

const SignalManagementPopup: React.FC<SignalManagementPopupProps> = ({
  isOpen, onClose, updates, onImportSelected, onRefresh, isLoading, allHistoryData
}) => {
  // ✅ 2. เรียกใช้ userPortfolios จาก Context แทนการรับ Props
  const { userPortfolios } = usePortfolio();

  const [activeTab, setActiveTab] = useState<TabType>('updates');
  const [selectedTickers, setSelectedTickers] = useState<string[]>([]);
  const [popupSearchTerm, setPopupSearchTerm] = useState('');
  
  const [hiddenTickers, setHiddenTickers] = useState<string[]>(() => {
    const saved = localStorage.getItem('ticker_hide');
    return saved ? JSON.parse(saved) : [];
  });

  const isNewSignal = (dateString: string) => {
    if (!dateString) return false;
    const now = new Date().getTime();
    const updatedTime = new Date(dateString).getTime();
    const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
    return (now - updatedTime) < twentyFourHoursInMs;
  };

  // ✅ 3. ปรับปรุงให้รองรับทั้ง V1 (stocks) และ V2 (holdings)
  const myTickerList = useMemo(() => {
    const tickers = new Set<string>();
    userPortfolios.forEach(p => {
      p.subPorts?.forEach((sp: any) => {
        const items = sp.holdings || sp.stocks || [];
        items.forEach((s: any) => {
          // คริปโตในพอร์ตเก็บเป็น BTC/USD แต่สัญญาณใช้ X:BTCUSD → เทียบด้วย holdingQuoteSymbol
          const sym = holdingQuoteSymbol(s);
          if (sym) tickers.add(sym);
        });
      });
    });
    return Array.from(tickers);
  }, [userPortfolios]); // ผูกกับ userPortfolios จาก Context

  const tabCounts = useMemo(() => {
    // 🌟 2.1 เพิ่มการสกัดข้อมูลซ้ำ! (เก็บเฉพาะหุ้นละ 1 ตัว)
    const uniqueUpdates = (updates || []).filter((v, i, a) => a.findIndex(t => t.ticker === v.ticker) === i);
    const uniqueAll = (allHistoryData || []).filter((v, i, a) => a.findIndex(t => t.ticker === v.ticker) === i);

    // 2.2 กรองตัวที่ไม่ถูกซ่อนของแต่ละก้อนข้อมูล
    const notHiddenUpdates = uniqueUpdates.filter(u => !hiddenTickers.includes(u.ticker));
    const notHiddenAll = uniqueAll.filter(u => !hiddenTickers.includes(u.ticker));

    return {
      updates: notHiddenUpdates.length,
      all_history: notHiddenAll.length,
      my_portfolio: notHiddenAll.filter(u => myTickerList.includes(u.ticker)).length,
      hidden: hiddenTickers.length
    };
  }, [updates, allHistoryData, hiddenTickers, myTickerList]);

  const visibleUpdates = useMemo(() => {
    const dataSource = (activeTab === 'all_history' || activeTab === 'my_portfolio')
      ? (allHistoryData || [])
      : (updates || []);

    // 🌟 เพิ่มการสกัดข้อมูลซ้ำสำหรับรายการที่จะเอาไป Render!
    const uniqueData = dataSource.filter((v, i, a) => a.findIndex(t => t.ticker === v.ticker) === i);

    let list = [...uniqueData];

    // กรองตามเงื่อนไขแท็บปกติ...
    if (activeTab === 'my_portfolio') {
      list = list.filter(u => myTickerList.includes(u.ticker));
    }

    if (activeTab !== 'hidden') {
      list = list.filter(u => !hiddenTickers.includes(u.ticker));
    } else {
      // ถ้าอยู่แท็บ 'hidden' ให้แสดงเฉพาะตัวที่ถูกซ่อนโดยไม่ซ้ำกัน
      return hiddenTickers.map(t => ({ ticker: t }));
    }

    if (popupSearchTerm) {
      list = list.filter(u => `${u.ticker} ${cryptoPairLabel(u.ticker)}`.toLowerCase().includes(popupSearchTerm.toLowerCase()));
    }

    return list;
  }, [updates, allHistoryData, activeTab, hiddenTickers, popupSearchTerm, myTickerList]);

  if (!isOpen) return null;

  const hideTicker = (ticker: string) => {
    const newList = [...new Set([...hiddenTickers, ticker])];
    setHiddenTickers(newList);
    localStorage.setItem('ticker_hide', JSON.stringify(newList));
    setSelectedTickers(prev => prev.filter(t => t !== ticker));
    toast.success(`ซ่อน ${cryptoPairLabel(ticker)} เรียบร้อย`);
  };

  const unhideTicker = (ticker: string) => {
    const newList = hiddenTickers.filter(t => t !== ticker);
    setHiddenTickers(newList);
    localStorage.setItem('ticker_hide', JSON.stringify(newList));
  };

  const toggleSelect = (ticker: string) => {
    setSelectedTickers(prev =>
      prev.includes(ticker) ? prev.filter(t => t !== ticker) : [...prev, ticker]
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center  bg-black/80 backdrop-blur-md" onClick={onClose}>
      <div className="bg-[#1c1c1e] border border-gray-800 w-full max-w-2xl h-[80vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>

        {/* Header & Tabs */}
        <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-[#1c1c1e]">
          <div className="flex gap-2 py-2 px-1 overflow-x-auto no-scrollbar">
            {[
              { id: 'updates', label: 'อัปเดตใหม่', count: tabCounts.updates, color: 'green' },
              { id: 'all_history', label: 'ดูทั้งหมด', count: tabCounts.all_history, color: 'yellow' },
              { id: 'my_portfolio', label: 'หุ้นในพอร์ต', count: tabCounts.my_portfolio, color: 'blue' },
              { id: 'hidden', label: 'ที่ซ่อนไว้', count: tabCounts.hidden, color: 'red' }
            ].map(tab => {
              const isActive = activeTab === tab.id;

              const colorConfig = {
                green: { bg: 'bg-green-500/10', border: 'border-green-500/20', text: 'text-green-400', active: 'bg-green-600' },
                yellow: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', text: 'text-yellow-400', active: 'bg-yellow-600' },
                blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400', active: 'bg-blue-600' },
                red: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', active: 'bg-red-600' }
              };

              const colors = colorConfig[tab.color as keyof typeof colorConfig];

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`
          relative flex items-center gap-2.5 px-4 py-2 rounded-xl border transition-all duration-300 whitespace-nowrap
          ${isActive
                      ? `${colors.active} border-transparent text-white shadow-lg shadow-black/40 scale-105 z-10`
                      : `${colors.bg} ${colors.border} ${colors.text} hover:bg-white/5 opacity-70`
                    }
        `}
                >
                  <span className="text-xs font-black tracking-wide uppercase">{tab.label}</span>

                  {/* ตัวเลข Badge */}
                  <span className={`
          text-[10px] font-black px-2 py-0.5 rounded-lg
          ${isActive ? 'bg-white/20 text-white' : 'bg-black/30 text-current'}
        `}>
                    {tab.count.toLocaleString()}
                  </span>

                  {/* เส้นแสงด้านล่าง (กรณี Active) */}
                  {isActive && (
                    <div
                      className="absolute -bottom-1 left-0 right-0 h-[8px] bg-white/20 blur-[3px] rounded-full mx-1.5"
                      style={{
                        boxShadow: '0 0 10px 2px rgba(255, 255, 255, 0.2)'
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white">✕</button>
        </div>

        {/* Search & Refresh Bar */}
        {activeTab !== 'hidden' && (
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800/50 bg-black/10">
            <button
              onClick={() => {
                const isFullSearch = activeTab === 'all_history' || activeTab === 'my_portfolio';
                onRefresh(isFullSearch);
              }} disabled={isLoading}
              title="อัปเดตข้อมูลใหม่"
              className={`p-1.5 rounded-lg bg-gray-800/50 border border-gray-700 text-xs ${isLoading ? 'animate-spin opacity-50' : ''}`}
            >
              🔄
            </button>
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="ค้นหาชื่อหุ้น..."
                value={popupSearchTerm.toUpperCase()}
                onChange={(e) => setPopupSearchTerm(e.target.value.toUpperCase())}
                className="w-full bg-gray-900/50 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-blue-500 transition-all uppercase"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 opacity-30 text-xs">🔍</span>
            </div>
          </div>
        )}
        {/* List Content */}
        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
          {activeTab !== 'hidden' ? (
            visibleUpdates.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {visibleUpdates.map(u => {
                  const supports = u.details?.lines
                    ?.filter((l: any) => l.type === 'support')
                    .sort((a: any, b: any) => b.price - a.price)
                    .map((l: any) => `$${l.price.toFixed(1)}`);

                  const resistances = u.details?.lines
                    ?.filter((l: any) => l.type === 'resistance')
                    .sort((a: any, b: any) => a.price - b.price)
                    .map((l: any) => `$${l.price.toFixed(1)}`);

                  const isSelected = selectedTickers.includes(u.ticker);

                  return (
                    <div
                      key={u.ticker}
                      className={`flex flex-col p-3 rounded-xl border transition-all relative ${isSelected
                        ? 'bg-blue-500/10 border-blue-500/50 shadow-[0_0_15px_rgba(59,130,246,0.1)]'
                        : 'bg-black/40 border-gray-800'
                        }`}
                    >
                      {/* ป้าย NEW */}
                      {isNewSignal(u.details?.lastUpdated || u.created_at) && (
                        <div className="absolute -top-1 -right-1 z-10">
                          <span className="bg-green-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded shadow-lg border border-green-500 animate-pulse uppercase">
                            New
                          </span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-black text-lg tracking-tight text-white leading-none">{cryptoPairLabel(u.ticker)}</span>
                        <span className="text-[9px] text-gray-600 bg-gray-800/50 px-1.5 py-0.5 rounded leading-none">
                          {new Date(u.details?.lastUpdated || u.created_at).toLocaleTimeString('th-TH', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>

                      <div className="flex-1 space-y-1 mb-3">
                        <div className="text-[10px] leading-tight">
                          <span className="text-green-500 font-bold">รับ: </span>
                          <span className="text-gray-400 break-words">{supports?.length > 0 ? supports.join(', ') : '-'}</span>
                        </div>
                        <div className="text-[10px] leading-tight">
                          <span className="text-red-500 font-bold">ต้าน: </span>
                          <span className="text-gray-400 break-words">{resistances?.length > 0 ? resistances.join(', ') : '-'}</span>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-auto pt-2 border-t border-gray-800/50">
                        <button
                          onClick={() => hideTicker(u.ticker)}
                          className="flex-1 py-1.5 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white rounded-md text-[10px] font-bold transition-all"
                        >
                          ซ่อน
                        </button>
                        <button
                          onClick={() => toggleSelect(u.ticker)}
                          className={`flex-[2] py-1.5 rounded-md text-[10px] font-bold transition-all ${isSelected
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                            }`}
                        >
                          {isSelected ? '✓ เลือกแล้ว' : 'เลือก'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20 opacity-30 text-sm">ไม่พบรายการอัปเดต</div>
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {hiddenTickers.map(ticker => (
                <div key={ticker} className="flex items-center justify-between p-3 bg-red-500/5 border border-red-500/20 rounded-xl">
                  <span className="font-bold text-gray-300 text-sm">{cryptoPairLabel(ticker)}</span>
                  <button
                    onClick={() => unhideTicker(ticker)}
                    className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-white rounded-md text-[10px] font-bold"
                  >
                    ยกเลิก
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {activeTab !== 'hidden' && (
          <div className="p-4 border-t border-gray-800 bg-[#1c1c1e] space-y-3">
            {selectedTickers.length > 0 && (
              <div className="flex flex-wrap gap-1 items-center mb-1">
                <span className="text-[10px] text-gray-500 uppercase font-bold mr-2">เลือกแล้ว:</span>
                {selectedTickers.slice(0, 5).map(t => (
                  <span key={t} className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] rounded-full border border-blue-500/30">{t}</span>
                ))}
                {selectedTickers.length > 5 && <span className="text-[10px] text-gray-600">+{selectedTickers.length - 5}...</span>}
              </div>
            )}

            <div className="flex gap-3">
              {(() => {
                const visibleTickers = visibleUpdates.map(u => u.ticker);
                const isAllVisibleSelected = visibleTickers.length > 0 &&
                  visibleTickers.every(t => selectedTickers.includes(t));

                return (
                  <button
                    onClick={() => {
                      if (isAllVisibleSelected) {
                        setSelectedTickers(prev => prev.filter(t => !visibleTickers.includes(t)));
                      } else {
                        setSelectedTickers(prev => Array.from(new Set([...prev, ...visibleTickers])));
                      }
                    }}
                    className={`flex-1 py-3 px-4 rounded-xl font-bold text-xs transition-all border active:scale-95 whitespace-nowrap ${isAllVisibleSelected
                      ? 'bg-red-500/10 border-red-500/50 text-red-500 hover:bg-red-500 hover:text-white shadow-[0_0_15px_rgba(239,68,68,0.1)]'
                      : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
                      }`}
                  >
                    {isAllVisibleSelected ? '✕ ยกเลิกการเลือก' : '✓ เลือกทั้งหมด'}
                  </button>
                );
              })()}

              <button
                disabled={selectedTickers.length === 0}
                onClick={() => {
                  const shouldUpdateSync = activeTab === 'updates';
                  onImportSelected(selectedTickers, true, shouldUpdateSync);
                  setSelectedTickers([]);
                  onClose();
                }}
                className="flex-[2] py-3 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-500 hover:to-blue-700 disabled:opacity-30 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-900/20 transition-all active:scale-[0.98]"
              >
                🚀 Import ที่เลือกไว้ ({selectedTickers.length})
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignalManagementPopup;